import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Container,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  RocketLaunch as RocketIcon,
  Star as StarIcon,
  ShieldMoon as ShieldIcon,
  Bolt as BoltIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../../store/authStore';
import useSubscriptionStore, { Plan } from '../../../store/subscriptionStore';
import { fetchPlans, createCheckoutSession } from '../api/subscriptionApi';
import { formatNumber } from '../../../utils/formatters';
import brand from '../../../config/brand';

const PLAN_ICONS: Record<string, React.ReactNode> = {
  scout: <BoltIcon />,
  cadet: <RocketIcon />,
  captain: <StarIcon />,
  air_marshal: <ShieldIcon />,
};

const PLAN_COLORS: Record<string, string> = {
  scout: '#78909c',
  cadet: '#42a5f5',
  captain: brand.colors.fire,
  air_marshal: '#ab47bc',
};

/** Static fallback so the page renders even before / without the API */
const FALLBACK_PLANS: Plan[] = [
  {
    key: 'scout',
    name: 'Scout',
    price: 0,
    priceAnnual: null,
    annualTotal: null,
    credits: 100000,
    monthlyAllocation: 0,
    features: ['100,000 CP sign-up bonus', 'Access to all models', 'Pay-as-you-go top-ups', 'Community support'],
    firstMonthPromo: null,
  },
  {
    key: 'cadet',
    name: 'Cadet',
    price: 19.99,
    priceAnnual: 15.99,
    annualTotal: 191.88,
    credits: 800000,
    monthlyAllocation: 1,
    features: ['800,000 CP/month', 'All models included', 'Credits roll over (up to 3x)', 'First month free', 'Priority support'],
    firstMonthPromo: 'Free',
  },
  {
    key: 'captain',
    name: 'Captain',
    price: 49.99,
    priceAnnual: 39.99,
    annualTotal: 479.88,
    credits: 2000000,
    monthlyAllocation: 2.5,
    features: ['2,000,000 CP/month', 'All models included', 'Credits roll over (up to 3x)', 'First month $1', 'Priority support', 'Advanced analytics'],
    firstMonthPromo: '$1',
  },
  {
    key: 'air_marshal',
    name: 'Air Marshal',
    price: 149.99,
    priceAnnual: 119.99,
    annualTotal: 1439.88,
    credits: 6600000,
    monthlyAllocation: 7.5,
    features: ['6,600,000 CP/month', 'All models included', 'Credits roll over (up to 3x)', 'Dedicated support', 'Advanced analytics', 'Early access to new features'],
    firstMonthPromo: null,
  },
];

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { plans: apiPlans, isLoadingPlans, error } = useSubscriptionStore();
  const [annual, setAnnual] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  // Use API plans if available, otherwise fall back to static data
  const plans = apiPlans && apiPlans.length > 0 ? apiPlans : FALLBACK_PLANS;

  useEffect(() => {
    fetchPlans().catch(() => {});
  }, []);

  const handleSubscribe = async (planKey: string) => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (planKey === 'scout') return;

    setCheckoutLoading(planKey);
    setCheckoutError(null);
    try {
      const { url } = await createCheckoutSession(planKey, annual ? 'annual' : 'monthly');
      if (url) window.location.href = url;
    } catch (err: any) {
      console.error('Checkout error:', err);
      const msg = err?.response?.data?.error || err?.message || 'Failed to start checkout';
      setCheckoutError(msg);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const getPrice = (plan: Plan) => {
    if (plan.price === 0) return 'Free';
    if (annual && plan.priceAnnual) return `$${plan.priceAnnual.toFixed(2)}/mo`;
    return `$${plan.price.toFixed(2)}/mo`;
  };

  const getCta = (plan: Plan) => {
    if (plan.key === 'scout') return 'Current Tier';
    if (plan.firstMonthPromo === 'Free') return 'Start Free Month';
    if (plan.firstMonthPromo) return `Start at ${plan.firstMonthPromo}`;
    return 'Subscribe';
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Checkout error banner */}
      {checkoutError && (
        <Alert severity="error" onClose={() => setCheckoutError(null)} sx={{ mb: 3 }}>
          {checkoutError}
        </Alert>
      )}

      {/* Header */}
      <Box textAlign="center" mb={5}>
        <Typography variant="h3" fontWeight={700} gutterBottom>
          Choose Your Plan
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
          Scale your AI development with credits that match your workflow.
          All plans include access to every model.
        </Typography>

        {/* Billing toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={annual}
              onChange={(e) => setAnnual(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography>{annual ? 'Annual' : 'Monthly'}</Typography>
              {annual && (
                <Chip label="Save ~20%" size="small" color="success" variant="outlined" />
              )}
            </Box>
          }
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {/* Plan Cards */}
      <Grid container spacing={3} justifyContent="center">
        {plans.map((plan) => {
          const color = PLAN_COLORS[plan.key] || '#78909c';
          const isPopular = plan.key === 'captain';
          return (
            <Grid item xs={12} sm={6} md={3} key={plan.key}>
              <Paper
                elevation={isPopular ? 8 : 2}
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: isPopular ? `2px solid ${color}` : '1px solid',
                  borderColor: isPopular ? color : 'divider',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
              >
                {isPopular && (
                  <Chip
                    label="Most Popular"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: color,
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                )}

                {/* Plan Header */}
                <Box textAlign="center" mb={2}>
                  <Box sx={{ color, mb: 1, '& svg': { fontSize: 36 } }}>
                    {PLAN_ICONS[plan.key]}
                  </Box>
                  <Typography variant="h5" fontWeight={700}>
                    {plan.name}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ mt: 1, color }}>
                    {getPrice(plan)}
                  </Typography>
                  {annual && plan.annualTotal && (
                    <Typography variant="caption" color="text.secondary">
                      ${plan.annualTotal}/year billed annually
                    </Typography>
                  )}
                  {plan.credits > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {formatNumber(plan.credits)} credits/month
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Features */}
                <List dense sx={{ flex: 1, py: 0 }}>
                  {plan.features.map((feature, i) => (
                    <ListItem key={i} disableGutters sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <CheckIcon sx={{ fontSize: 18, color }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>

                {/* CTA */}
                <Button
                  fullWidth
                  variant={plan.key === 'scout' ? 'outlined' : 'contained'}
                  disabled={plan.key === 'scout' || checkoutLoading === plan.key}
                  onClick={() => handleSubscribe(plan.key)}
                  sx={{
                    mt: 2,
                    py: 1.2,
                    fontWeight: 600,
                    ...(plan.key !== 'scout' && {
                      bgcolor: color,
                      '&:hover': { bgcolor: color, filter: 'brightness(0.9)' },
                    }),
                  }}
                >
                  {checkoutLoading === plan.key ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    getCta(plan)
                  )}
                </Button>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* PAYG Section */}
      <Box mt={8} textAlign="center">
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Need Extra Credits?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', mb: 4 }}>
          Top up anytime with pay-as-you-go credit packs. Works with any plan.
        </Typography>

        <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: 700, mx: 'auto' }}>
          {[
            { key: 'pack_5', amount: 5, credits: '100,000' },
            { key: 'pack_10', amount: 10, credits: '200,000' },
            { key: 'pack_25', amount: 25, credits: '500,000' },
          ].map((pack) => (
            <Grid item xs={12} sm={4} key={pack.key}>
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
                }}
              >
                <Typography variant="h5" fontWeight={700}>
                  ${pack.amount}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {pack.credits} credits
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 2 }}
                  onClick={() => {
                    if (!user) {
                      navigate('/login');
                      return;
                    }
                    navigate('/payment', { state: { payg: pack.key } });
                  }}
                >
                  Buy Now
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default PricingPage;
